import { Box, Flex, Text, Divider, VStack, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, Button, useToast } from "@chakra-ui/react";
import { useCallback, useRef, useState } from "react";

import { $ok } from "foundation-ts/commons";
import { Nullable } from "foundation-ts/types";

import { G1Button } from "@components/G1Button";
import { MethodTab } from "@components/Payment/MethodTab";
import { CreditCardCard } from "@/components/Payment/CreditCardCard";
import { SepaCard } from "@components/Payment/SepaCard";
import { MollieInstance, PaymentFormRef, PaymentMethodType } from "@components/Payment/types";
import { optlog } from "@utils/functions";
import { BankIcon, CardIcon } from "@utils/icons";
import { defaultErrorToast } from "@utils/toast";

import { formatDate, maskCard, maskIban } from "./helpers";
import { Mandate } from "./types";
import { Card } from "./UI";

interface MandateCardProps {
    mandate: Nullable<Mandate>;
    isCancelationRequested: boolean;
    companyName: string;
    testMode: boolean;
    mollie: Nullable<MollieInstance>;
    mollieReady: boolean;
    onCardSubmit: (token: string) => Promise<void>;
    onMandateSubmit: (consumerAccount: string, consumerName: string) => Promise<void>;
}

export function MandateCard(props: MandateCardProps) {
    const { mandate, companyName, testMode, mollie, mollieReady, isCancelationRequested, onCardSubmit, onMandateSubmit } = props;
    const mandateModal = useDisclosure();
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<Nullable<PaymentMethodType>>(null);
    const [paymentFormValid, setPaymentFormValid] = useState(false);
    const [loading, setLoading] = useState(false);
    const paymentFormRef = useRef<PaymentFormRef>(null);
    const toast = useToast();

    const isCard = $ok(mandate?.cardNumber);

    const openMandateModal = (): void => {
        setSelectedPaymentMethod("sepa");
        mandateModal.onOpen();
    }

    const onMethodChange = (method: PaymentMethodType): void => {
        setSelectedPaymentMethod(method);
        setPaymentFormValid(false);
    }

    const handleSubmit = useCallback(async () => {
        try {
            setLoading(true);
            await paymentFormRef.current?.submit();
        } catch (e) {
            optlog(`${e}`);
            toast(defaultErrorToast("Une erreur inattendue est survenue lors du changement de mandat"));
        } finally {
            setLoading(false);
            mandateModal.onClose();
        }
    }, []);

    return (
        <>
            <Card>
                <Flex justify="space-between" align="flex-start" flexWrap="wrap" mb={5} gap={3}>
                    <Text fontFamily="heading" fontSize="lg" color="gray.900">
                        {isCard ? "Carte bancaire" : "Mandat SEPA"}
                    </Text>
                    <Button size="xs" variant="outline" borderColor="brand.500" color="brand.500"_hover={{ bg: "brand.50" }}
                            isDisabled={isCancelationRequested} onClick={openMandateModal}>
                        Mettre à jour
                    </Button>
                </Flex>

                <VStack spacing={4} align="stretch">
                    <Box bg="surface.subtle" borderRadius="12px" p={4}>
                        <Text fontSize="xs" color="gray.400" mb={1} textTransform="uppercase" letterSpacing="wider">Titulaire</Text>
                        <Text fontSize="sm" fontWeight="600" color="gray.800">{mandate?.holder}</Text>
                    </Box>

                    {isCard ? (
                        <>
                            <Box>
                                <Text fontSize="xs" color="gray.400" mb={1} textTransform="uppercase" letterSpacing="wider">Numéro de carte</Text>
                                <Text fontSize="sm" fontWeight="600" color="gray.800" fontFamily="mono" letterSpacing="wider">
                                    {maskCard(mandate?.cardNumber ?? "????")}
                                </Text>
                            </Box>
                            {$ok(mandate?.cardExpiryDate) && (
                                <Box>
                                    <Text fontSize="xs" color="gray.400" mb={1} textTransform="uppercase" letterSpacing="wider">Expiration</Text>
                                    <Text fontSize="sm" fontWeight="600" color="gray.800" fontFamily="mono">{mandate?.cardExpiryDate}</Text>
                                </Box>
                            )}
                        </>
                    ) : (
                        <Box>
                            <Text fontSize="xs" color="gray.400" mb={1} textTransform="uppercase" letterSpacing="wider">IBAN</Text>
                            <Text fontSize="sm" fontWeight="600" color="gray.800" fontFamily="mono" letterSpacing="wider" wordBreak="break-all">
                                {maskIban(mandate?.consumerAccount!)}
                            </Text>
                        </Box>
                    )}

                    <Flex gap={6} wrap="wrap">
                        <Box flex={1} minW="100px">
                            <Text fontSize="xs" color="gray.400" mb={1} textTransform="uppercase" letterSpacing="wider">Référence</Text>
                            <Text fontSize="xs" fontWeight="500" color="gray.600" wordBreak="break-all">{mandate?.reference}</Text>
                        </Box>
                    </Flex>

                    <Divider borderColor="gray.100" />
                    <Text fontSize="xs" color="gray.400" textAlign="center">Mis à jour le {formatDate(mandate?.signatureDate)}</Text>
                </VStack>
            </Card>

            <Modal isOpen={mandateModal.isOpen} onClose={mandateModal.onClose} size="lg" isCentered>
                <ModalOverlay backdropFilter="blur(6px)" />
                <ModalContent borderRadius="20px" p={2}>
                    <ModalHeader fontFamily="heading">Mettre à jour votre mandat</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={5} align="stretch">
                            <Box>
                                <Flex gap={3}>
                                    <MethodTab
                                        icon={<BankIcon />}
                                        label="Virement SEPA"
                                        description="Prélèvement automatique"
                                        colors={{
                                            border: selectedPaymentMethod === "sepa" ? "brand.500" : "gray.100",
                                            bg: selectedPaymentMethod === "sepa" ? "brand.50" : "white",
                                            iconBg: selectedPaymentMethod === "sepa"  ?  "brand.500" : "gray.400",
                                            textLabel: selectedPaymentMethod === "sepa" ? "brand.700" : "gray.700",
                                             textDesc: selectedPaymentMethod === "sepa" ? "brand.400" : "gray.400"
                                        }}
                                        onClick={() => { onMethodChange("sepa") }}
                                    />
                                    <MethodTab
                                        icon={<CardIcon />}
                                        label="Carte bancaire"
                                        description="CB, Visa, Mastercard"
                                        colors={{
                                            border: selectedPaymentMethod === "card" ? "brand.500" : "gray.100",
                                            bg: selectedPaymentMethod === "card" ? "brand.50" : "white",
                                            iconBg: selectedPaymentMethod === "card"  ?  "brand.500" : "gray.400",
                                            textLabel: selectedPaymentMethod === "card" ? "brand.700" : "gray.700",
                                             textDesc: selectedPaymentMethod === "card" ? "brand.400" : "gray.400"
                                        }}
                                        onClick={() => { onMethodChange("card") }}
                                    />
                                </Flex>
                            </Box>

                            <Box bg="orange.50" borderRadius="12px" p={3} border="1px solid" borderColor="orange.100">
                                <Text fontSize="xs" color="orange.700">
                                    {selectedPaymentMethod === "sepa"
                                        ? "Un nouveau mandat remplacera l'actuel et prendra effet sur votre prochain prélèvement."
                                        : "La nouvelle carte remplacera le moyen de paiement actuel dès validation."}
                                </Text>
                            </Box>

                            <Box key={selectedPaymentMethod} p={6} borderRadius="xl" border="1px solid" borderColor="gray.600" bg="prussianBlue.500">
                                {selectedPaymentMethod === "card" && (
                                    <CreditCardCard
                                        ref={paymentFormRef}
                                        labelColor="gray.100"
                                        mollie={mollie}
                                        mollieReady={mollieReady}
                                        onSubmit={onCardSubmit}                                  
                                        onValidityChange={setPaymentFormValid}
                                    />
                                )}
                                {selectedPaymentMethod === "sepa" && (
                                    <SepaCard
                                        ref={paymentFormRef}
                                        companyName={companyName}
                                        labelColor="gray.100"
                                        testmode={testMode}
                                        onSubmit={onMandateSubmit}
                                        onValidityChange={setPaymentFormValid}
                                    />
                                )}
                            </Box>
                            <Text fontSize="xs" color="gray.400" textAlign="center">
                                Vos données bancaires sont transmises de manière sécurisée vers notre sous-traitant Mollie
                            </Text>
                        </VStack>
                    </ModalBody>
                    <ModalFooter gap={2} justifyItems="stretch">
                        <G1Button component="form" variant="cancel" title="Annuler" flex={1} onClick={mandateModal.onClose} />
                        <G1Button component="form" variant="ok" title="Enregistrer" flex={1} disabled={!paymentFormValid} isLoading={loading} onClick={handleSubmit} />
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}